import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import moment from 'moment-timezone';
import PropTypes, { any } from 'prop-types';

import { required } from '../../../../utils/form-validation';
import {
    TitleSwitch,
    DateTimePicker,
} from '../../../Form';

const SelfWithdraw = (props) => {
    const {
        formApi,
        coreUI,
    } = props;

    const { values, error, setValue } = formApi;

    const [showSelfWithdraw, setShowSelfWithdraw] = useState(values?.selfWithdrawAllowed || false);
    const [maxSelfWithdrawDate, setMaxSelfWithdrawDate] = useState();
    const [showAutoRefund, setShowAutoRefund] = useState(values?.autoRefundEnabled || false);
    const [maxAutoRefundDate, setMaxAutoRefundDate] = useState();
    const [tourDateEnd, setTourDateEnd] = useState();
    const [regDateStart, setRegDateStart] = useState();
    const [regDateEnd, setRegDateEnd] = useState();

    useEffect(() => {
        if (values?.eventSchedule) {
            const { eventSchedule, maxSelfWithdrawDateUtc, maxAutoRefundDateUtc } = values;
            const timeZone = eventSchedule.timeZone || 77;
            const timeZoneObj = coreUI.types.timeZones.find(tz => { return tz.id === parseInt(timeZone); });

            setRegDateStart(moment.utc(eventSchedule.registrationStartUtc).tz(timeZoneObj.value));
            setRegDateEnd(moment.utc(eventSchedule.registrationEndUtc).tz(timeZoneObj.value));
            setTourDateEnd(moment.utc(eventSchedule.eventEndUtc).tz(timeZoneObj.value));
            if (maxSelfWithdrawDateUtc && !maxSelfWithdrawDate) {
                setMaxSelfWithdrawDate(moment.utc(maxSelfWithdrawDateUtc).tz(timeZoneObj.value));
            }
            if (maxAutoRefundDateUtc && !maxAutoRefundDate) {
                setMaxAutoRefundDate(moment.utc(maxAutoRefundDateUtc).tz(timeZoneObj.value));
            }
        }
    }, [values, coreUI.types.timeZones]);

    const handleDateChange = (key, date) => {
        if (key === 'maxSelfWithdrawDateUtc' && date?.isBefore(tourDateEnd)) {
            setMaxSelfWithdrawDate(date);
            setValue('selfWithdrawAllowed', true);
            setValue('maxSelfWithdrawDateUtc', date.toISOString());
            if (showSelfWithdraw && maxAutoRefundDate?.isAfter(date)) {
                setMaxAutoRefundDate(date);
                setValue('maxAutoRefundDateUtc', date.toISOString());
            }
        }
        if (key === 'maxAutoRefundDateUtc' && date?.isBefore(tourDateEnd)) {
            setMaxAutoRefundDate(date);
            setValue('autoRefundEnabled', true);
            setValue('maxAutoRefundDateUtc', date.toISOString());
        }
        formApi.validate(formApi.values);
    };

    const toggleSelfWithdraw = () => {
        if (showSelfWithdraw) {
            setShowSelfWithdraw(!showSelfWithdraw);
            setValue('selfWithdrawAllowed', false);
            setMaxSelfWithdrawDate();
            formApi.reset('maxSelfWithdrawDateUtc');
            // also reset autorefund
            if (showAutoRefund) {
                setShowAutoRefund(!showAutoRefund);
                setValue('autoRefundEnabled', false);
                setMaxAutoRefundDate();
                formApi.reset('maxAutoRefundDateUtc');
            }
            formApi.validate(formApi.values);
        } else {
            setShowSelfWithdraw(!showSelfWithdraw);
            setValue('selfWithdrawAllowed', true);
            // default to registration end date
            setMaxSelfWithdrawDate(regDateEnd);
            setValue('maxSelfWithdrawDateUtc', regDateEnd.toISOString());
        }
    };

    const toggleAutoRefund = () => {
        if (showAutoRefund) {
            setShowAutoRefund(!showAutoRefund);
            setValue('autoRefundEnabled', false);
            formApi.reset('maxAutoRefundDateUtc');
            formApi.validate(formApi.values);
        } else {
            setShowAutoRefund(!showAutoRefund);
            setValue('autoRefundEnabled', true);
            setMaxAutoRefundDate(maxSelfWithdrawDate);
            setValue('maxAutoRefundDateUtc', maxSelfWithdrawDate.toISOString());
        }
    };

    return (
        <div className="col-12">
            <div className={`form-block-content`}>
                <div className="row">
                    <div className="col-12">
                        <div className="div div16" />
                        <TitleSwitch
                            id="selfWithdraw"
                            data-testid="event-edit.self-withdraw.toggle"
                            formGroupCssClass={`mt16`}
                            label={'Allow players to self-withdraw'}
                            description={`If enabled, players can withdraw themselves without contacting the tournament provider until the specified date. After this date, withdrawals can only be initiated by the tournament provider.`}
                            value={showSelfWithdraw}
                            onChange={toggleSelfWithdraw}
                        />
                    </div>
                </div>
                {showSelfWithdraw && (
                    <>
                        <div className="row">
                            <div className={`col-12 ${error?.regEnd ? 'is-invalid' : ''}`}>
                                <div className="bg-white p16">
                                    <DateTimePicker
                                        label="Max player self-withdraw date"
                                        id="maxSelfWithdrawDate"
                                        field="maxSelfWithdrawDateUtc"
                                        placeholderText="Select Date"
                                        selected={maxSelfWithdrawDate}
                                        minDate={regDateStart}
                                        maxDate={tourDateEnd}
                                        handleDateChange={(date) => handleDateChange('maxSelfWithdrawDateUtc', date)}
                                        validate={showSelfWithdraw && required}
                                        themeV3
                                        data-testid="event-edit.max-self-withdraw-date-date-picker"
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-12">
                                <div className="div div16 mt16" />
                                <TitleSwitch
                                    id="autoRefund"
                                    formGroupCssClass={`mt16`}
                                    label={'Refund self-withdrawals automatically'}
                                    description={`If enabled, players will be refunded their registration fees when they self-withdraw up until the specified date. This date cannot be after the max self-withdraw date.
                                    Note: If a registration withdraw fee has been set, players will still be refunded minus the withdraw fee amount.`}
                                    value={showAutoRefund}
                                    onChange={toggleAutoRefund}
                                        data-testid="event-edit.auto-refund-title-switch"
                                />
                            </div>
                        </div>
                        {showAutoRefund && (
                            <div className="row">
                                <div className={`col-12 ${error?.regEnd ? 'is-invalid' : ''}`}>
                                    <div className="bg-white p16">
                                        <DateTimePicker
                                            label="Max Auto-Refund Date"
                                            id="maxAutoRefundDate"
                                            field="maxAutoRefundDateUtc"
                                            placeholderText="Select Date"
                                            selected={maxAutoRefundDate}
                                            minDate={regDateStart}
                                            maxDate={maxSelfWithdrawDate}
                                            handleDateChange={(date) => handleDateChange('maxAutoRefundDateUtc', date)}
                                            validate={required}
                                            themeV3
                                                data-testid="event-edit.max-auto-refund-date-date-picker"
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

SelfWithdraw.propTypes = {
    formApi: PropTypes.objectOf(any).isRequired,
    coreUI: PropTypes.objectOf(any).isRequired,
};

function mapStateToProps({ coreUI }) {
    return { coreUI };
}

export default connect(mapStateToProps)(SelfWithdraw);
